import { useNavigate, useSearch } from '@tanstack/react-router';
import { useAuthContext } from '@emfood/shared';
import Iconify from '@emfood/shared/src/components/iconify';

import {
  Box,
  Card,
  Link,
  Stack,
  Tooltip,
  Divider,
  Typography,
  IconButton,
  CardContent,
} from '@mui/material';

import { paths } from 'src/routes/paths';

import PriceLabel from 'src/components/products/price-label';
import { QuantityInput } from 'src/components/products/quantity-input';
import AddToCartButton from 'src/components/products/add-to-cart-button';
import SurplusProductLabel from 'src/components/products/surplus-product-label';
import UpdateProductButton from 'src/components/products/update-product-button';
import PromotionalProductLabel from 'src/components/products/promotional-product-label';

import { ActiveOperations } from './actions-cell';
import { EnhancedFavouritesItemModel } from './use-favourite-products-list';

interface MobileProductCardProps {
  product: EnhancedFavouritesItemModel;
  handleQuantityChange: (productStockId: string, delta: number) => void;
  handleUpdate: (cartItemId: string, quantity: number, productId: string) => void;
  handleAddToCart: (productStockId: string, quantity: number, productId: string) => void;
  handleRemoveFromCart: (cartItemId: string, productId: string) => void;
  deleteItem: (itemId: string) => Promise<void>;
  activeOperations: ActiveOperations;
}

export default function FavouriteProductsListCard({
  product,
  handleQuantityChange,
  handleUpdate,
  handleAddToCart,
  handleRemoveFromCart,
  deleteItem,
  activeOperations,
}: MobileProductCardProps) {
  const thisRowOp = activeOperations.get(product.id);
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const searchParams = useSearch({ from: '/favourites/' });
  const currentTab = searchParams.tab;

  const isCateringManager =
    user?.roles?.some((role: any) => role.name === 'Catering Manager') || false;
  const canModify = currentTab === 'personal' || isCateringManager;

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ p: 2, flex: 1 }}>
        <Stack spacing={2} height="100%">
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            {product.surplusEntry?.typeOfPrice === 'surplus' && <SurplusProductLabel />}
            {product.surplusEntry?.typeOfPrice === 'promotion' && <PromotionalProductLabel />}
          </Stack>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Stack spacing={1} flex={1}>
              <Link
                component="button"
                onClick={() =>
                  navigate({
                    to: paths.dashboard.products.productDetails(product.productStockId),
                  })
                }
              >
                <Typography variant="h3" color="text.primary" sx={{ textAlign: 'left' }}>
                  {product.name}
                </Typography>
                <Typography variant="h4" color="GrayText" align="left">
                  {product.units}
                </Typography>
              </Link>
              {product.producerId ? (
                <Link
                  component="button"
                  onClick={() =>
                    navigate({
                      to: paths.dashboard.producer.producerDetails(product.producerId!),
                    })
                  }
                >
                  <Typography variant="body2" color="textPrimary" align="left">
                    {product.producerName}
                  </Typography>
                </Link>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left' }}>
                  {product.brand || '---'}
                </Typography>
              )}
            </Stack>

            {canModify && (
              <Tooltip title="Remove from favourites">
                <IconButton onClick={() => deleteItem(product.id)}>
                  <Iconify icon="solar:minus-circle-bold" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>

          <Stack spacing={1}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Price:
              </Typography>
              <PriceLabel
                direction="row"
                price={product.originalPrice.toFixed(2)}
                surplusEntry={product.surplusEntry}
              />
            </Stack>

            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" color="text.secondary">
                Quantity:
              </Typography>
              <QuantityInput
                productName={product.name}
                value={product.quantity}
                onChange={(value) =>
                  handleQuantityChange(product.productStockId, value - product.quantity)
                }
                minimumValue={1}
                maximumValue={Number(product.surplusEntry?.reservedQuantity)}
              />
            </Stack>

            {product.surplusEntry?.reservedQuantity && (
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  Available Stock:
                </Typography>
                <Typography variant="body2">{product.surplusEntry.reservedQuantity}</Typography>
              </Stack>
            )}

            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Total Price:
              </Typography>
              <Typography variant="body2">£{Number(product.totalPrice).toFixed(2)}</Typography>
            </Stack>
          </Stack>

          <Box sx={{ mt: 'auto' }}>
            <Divider sx={{ my: 2 }} />

            {product.itemInCart ? (
              <Stack direction="row" spacing={1} width="100%">
                <UpdateProductButton
                  isLoading={thisRowOp === 'update'}
                  isDisabled={product.quantity.toString() === product.cartQuantity.toString()}
                  onClick={() => handleUpdate(product.cartItemId, product.quantity, product.id)}
                  quantity={product.quantity}
                  maxQuantity={Number(product.surplusEntry?.reservedQuantity)}
                />

                <Tooltip title="Remove from basket">
                  <IconButton
                    aria-label="Remove from basket"
                    onClick={() => handleRemoveFromCart(product.cartItemId, product.id)}
                    disabled={thisRowOp === 'remove'}
                  >
                    <Iconify icon="solar:trash-bin-trash-bold" />
                  </IconButton>
                </Tooltip>
              </Stack>
            ) : (
              <AddToCartButton
                isLoading={thisRowOp === 'add'}
                onClick={() =>
                  handleAddToCart(product.productStockId, product.quantity, product.id)
                }
                quantity={product.quantity}
                maxQuantity={Number(product.surplusEntry?.reservedQuantity)}
              />
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}
